import React, { Component } from "react";
import { hideLoading, LoadableDataState, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { Lookup, TenantUserProfile } from "@eres/ejari-api/Entity";
import PageContent from "@my-dld/components/PageContent";
import Strings from "@my-dld/assets/strings";
import FormRow from "@eres/forms-web/FormRow";
import RadioInputField from "@eres/forms-web/RadioInputField";
import { IdExtractor, IdExtractorFallback, lookupConverter, serverDateConverter } from "@my-dld/utils/LookupUtil";
import { dualDataManager } from "@eres/forms/valuemanager/DualDataManager";
import TextInputField from "@eres/forms-web/TextInputField";
import { DisableEditOrgDataProvider } from "@eres/forms/providers/editable/OrgDataEditProvider";
import DateInputField from "@eres/forms-web/DateInputField";
import MobileInputField from "@eres/forms-web/MobileInputField";
import localMobileConverter from "@eres/forms/converters/LocalMobileConverter";
import FormSection from "@my-dld/components/Form/FormSection";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { defaultErrorManager } from "@eres/forms/errormanager";
import PickerInput from "@eres/forms-web/PickerInput";
import EidInputField from "@eres/forms-web/EidInputField";
import NumericStringConverter from "@eres/forms/converters/NumericStringConverter";
import FormAction from "@my-dld/components/Form/FormAction";
import { AlphaSortProcessor, CountriesTransformer, populateLookupOnTo, StateConsumer } from "@eres/ejari-api/Utils";
import { EjariLookUp } from "@eres/ejari-api/constants";
import _ from "lodash";
import { TabuParticipant, TabuRequestApi } from "@10x/tabu-mobile-api";
import { EjariCoreConfig, TenancyContactAction } from "@eres/ejari-core/constants";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "../../../utils";
import { StringUtil } from "@eres/core";
import { TenantRegiModel } from "@eres/ejari-api/Entity/tenant";
import I8N from "@eres/core/dist/I8N";
import { normalizeTenantRegistration } from "@eres/ejari-api/ContractUtils";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { TenantContext } from "@eres/ejari-core/contexts/TenantContext";
import { resetTenantContractContext, updateTenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import { dispatch, replaceNavigate } from "@my-dld/utils/StoreUtils";
import ConfirmationPopup from "@my-dld/components/Popup/ConfirmationPopup";
import { EjariApi } from "@eres/ejari-api";
import Actions from "@my-dld/actions";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import eidConverter from "@eres/forms/converters/EidConverter";

interface LookupState {
	Genders?: Lookup[];
	Countries?: Lookup[];
}

interface State extends LookupState, LoadableDataState {
	newData?: TenantRegiModel;
	orgData?: TenantRegiModel;
	isGCC: boolean;
	showConfirmation?: boolean;
	today: Date;
}

class TenantRegistration extends Component<RoutedViewProps, State> {
	static contextType = TenantContext;
	context!: React.ContextType<typeof TenantContext>;

	constructor( props: RoutedViewProps ) {
		super( props );
		this.state = {
			loadState: "loading",
			errorContainer: {},
			isGCC: false,
			today: new Date(),
		};
	}

	componentDidMount() {
		showLoading();
		populateLookupOnTo( EjariLookUp.GENDER, StateConsumer( this, "Genders" ) );
		populateLookupOnTo( EjariLookUp.COUNTRY, StateConsumer( this, "Countries" ), CountriesTransformer, AlphaSortProcessor );
		this.loadTabuParticipant();
	}

	render() {
		const { Genders, Countries, orgData, isGCC, showConfirmation, today } = this.state;
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-6 form-group",
			valueManager: dualDataManager,
			errorManager: defaultErrorManager,
		};
		const data = this.getUpdatedData();
		return <PageContent title={ Strings.Registration }>
			<PageTitle title={ Strings.register_certificate } paths={ [ DashboardBCItem ] }/>
			<React.Fragment>
				<FormSection title={ Strings.tenant_information }>
					<FormRow { ...filedProps }>
						<RadioInputField
							required={ true }
							id={ "Gender" }
							label={ Strings.gender }
							isLoading={ Genders == null }
							items={ Genders }
							toIdentifier={ IdExtractor }
							valueConverter={ lookupConverter }
							enableEditProvider={ DisableEditOrgDataProvider }
							disabled={ orgData && orgData.Gender !== undefined }/>
					</FormRow>
					<FormRow { ...filedProps }>
						<TextInputField
							required={ true }
							id={ "EnglishName" }
							label={ Strings.tenant_name_en }
							extraInputClassName={ "en-text" }
							enableEditProvider={ DisableEditOrgDataProvider }/>
						<TextInputField
							required={ true }
							id={ "ArabicName" }
							label={ Strings.tenant_name_ar }
							type={"arabic"}
							extraInputClassName={ "ar-text" }
							enableEditProvider={ DisableEditOrgDataProvider }/>
						<DateInputField
							required={ true }
							id={ "BirthDate" }
							label={ Strings.date_of_birth }
							valueConverter={ serverDateConverter }
							extraOpts={ {
								maxDate: today,
							} }
							disabled={ orgData && orgData.BirthDate !== undefined }/>
						<TextInputField
							required={ true }
							id={ "Email" }
							label={ Strings.email }
							enableEditProvider={ DisableEditOrgDataProvider }/>
						<MobileInputField
							required={ true }
							id={ "MobileNumber" }
							label={ Strings.mobile_number }
							valueConverter={ localMobileConverter }
							enableEditProvider={ DisableEditOrgDataProvider }/>
						<TextInputField
							required={ true }
							id={ "Pobox" }
							label={ Strings.po_box }
							enableEditProvider={ DisableEditOrgDataProvider }/>
					</FormRow>
				</FormSection>
				<FormSection
					title={ Strings.passport_information }
					extraClass={ "mt-2" }>
					<FormRow { ...filedProps }>
						<PickerInput
							required={ true }
							id={ "NationalityId" }
							label={ Strings.country }
							isLoading={ Countries === undefined }
							items={ Countries }
							toIdentifier={ IdExtractorFallback }
							valueConverter={ lookupConverter }
							onValueChange={ ( value, extra ) => {
								if ( value && value.ParentId === 1 ) {
									this.setState( { isGCC: true } );
								} else
									this.setState( { isGCC: false } );
							} }
							enableEditProvider={ DisableEditOrgDataProvider }/>
						<TextInputField
							required={ !isGCC }
							id={ "PassportNumber" }
							label={ Strings.passport_number }
							enableEditProvider={ DisableEditOrgDataProvider }/>
						<TextInputField
							required={ !isGCC }
							id={ "PassportIssuePlace" }
							label={ Strings.passport_issue_place }
							enableEditProvider={ DisableEditOrgDataProvider }/>
						<DateInputField
							required={ !isGCC }
							id={ "PassportIssueDate" }
							label={ Strings.passport_issue_date }
							extraOpts={ {
								maxDate: data.PassportExpiryDate?.asDate(),
							} }
							valueConverter={ serverDateConverter }/>
						<DateInputField
							required={ !isGCC }
							id={ "PassportExpiryDate" }
							label={ Strings.passport_exp_date }
							valueConverter={ serverDateConverter }
							extraOpts={ {
								minDate: data.PassportIssueDate?.asDate(),
							} }/>
					</FormRow>
				</FormSection>
				<FormSection
					title={ Strings.residence_information }
					extraClass={ "mt-2" }>
					<FormRow { ...filedProps }>
						<TextInputField
							required={ !isGCC }
							id={ "VisaNumber" }
							label={ Strings.residence_visa_number }
							valueConverter={ NumericStringConverter }
							enableEditProvider={ DisableEditOrgDataProvider }/>
						<DateInputField
							required={ !isGCC }
							id={ "VisaIssueDate" }
							label={ Strings.visa_start_date }
							valueConverter={ serverDateConverter }
							extraOpts={ {
								maxDate: data.VisaExpiryDate?.asDate(),
							} }
							enableEditProvider={ DisableEditOrgDataProvider }/>
						<DateInputField
							required={ !isGCC }
							id={ "VisaExpiryDate" }
							label={ Strings.visa_expiry_date }
							valueConverter={ serverDateConverter }
							enableEditProvider={ DisableEditOrgDataProvider }
							extraOpts={ {
								minDate: data.VisaIssueDate?.asDate(),
							} }/>
					</FormRow>
					<FormRow { ...filedProps }>
						<EidInputField
							required={ true }
							id={ "EmiratesId" }
							label={ Strings.emirates_id }
							valueConverter={ eidConverter }
							enableEditProvider={ DisableEditOrgDataProvider }/>
						<TextInputField
							required={ true }
							id={ "UIDNumber" }
							label={ Strings.unified_number }
							valueConverter={ NumericStringConverter }
							enableEditProvider={ DisableEditOrgDataProvider }/>
					</FormRow>
				</FormSection>
				<FormRow>
					<FormAction
						primaryActionLabel={ Strings.submit }
						onPrimaryAction={ this.onSubmit }
						secondaryActionLabel={ Strings.back }
						onSecondaryAction={ this.onBack }/>
				</FormRow>
			</React.Fragment>
			{ showConfirmation && <ConfirmationPopup
				primaryLabel={ Strings.continue }
				secondaryLabel={ Strings.back }
				message={ Strings.register_tenant_success }
				onConfirm={ this.startTenantContact }
				onCancel={ this.onHide }/> }
		</PageContent>;
	}

	private loadTabuParticipant() {
		TabuRequestApi.getTabuParticipant( EjariCoreConfig.getParticipantID()! ).then( ( resp ) => {
			this.normaliseRegisterModel( resp );
		} ).catch( ( reason ) => {
			hideLoading();
			showErrorMessage( reason.toString() );
		} );
	}

	private normaliseRegisterModel( participant: TabuParticipant ) {
		const info = participant.ParticipantEntity;
		let mobile = EjariCoreConfig.getMobileNumber();
		if ( mobile && !StringUtil.isEmptyStringOrNull( mobile ) ) {
			mobile = StringUtil.normaliseMobileNumber( mobile );
		}
		let regModel: TenantRegiModel;
		const placeEn = !StringUtil.isEmptyStringOrNull( participant.PassportIssuePlaceEn ) ?
			participant.PassportIssuePlaceEn : undefined;
		const placeAr = !StringUtil.isEmptyStringOrNull( participant.PassportIssuePlaceAr ) ?
			participant.PassportIssuePlaceAr : undefined;
		regModel = {
			Gender: ( participant.Sex === true ? 1 : 0 ),
			EnglishName: info?.NameEn,
			ArabicName: info?.NameAr,
			BirthDate: participant.BirthDate,
			Email: info?.Email,
			MobileNumber: mobile,
			Pobox: info?.PoBox,
			NationalityId: participant.CountryId,
			PassportNumber: participant.IdNumber,
			PassportIssuePlace: I8N.isRTL() ? placeAr : placeEn,
			PassportIssueDate: participant.IdIssueDate,
			PassportExpiryDate: participant.PassportExpiryDate ? participant.PassportExpiryDate : undefined,
			VisaNumber: participant.ResidencyNumber ? participant.ResidencyNumber : undefined,
			VisaIssueDate: participant.ResidencyStartDate ? participant.ResidencyStartDate : undefined,
			VisaExpiryDate: participant.ResidencyExpiryDate ? participant.ResidencyExpiryDate : undefined,
			EmiratesId: EjariCoreConfig.getEmiratesId(),
			UIDNumber: participant.UnifiedNumber ? participant.UnifiedNumber : undefined,
		};
		this.setState( {
			orgData: regModel,
			isGCC: participant.CountryId ? participant.CountryId <= 6 : false,
			loadState: "done",
		}, () => hideLoading() );
	}

	private getUpdatedData = ( state = this.state ) => {
		const data: TenantRegiModel = {};
		_.merge( data, state.orgData, state.newData );
		return data;
	};

	private isValid() {
		const data = this.getUpdatedData();
		const errorContainer: ErrorContainer = {};
		let isValid = checkNonEmpty( data, "Gender", errorContainer );
		isValid = checkNonEmpty( data, "EnglishName", errorContainer ) && isValid;
		isValid = checkNonEmpty( data, "ArabicName", errorContainer ) && isValid;
		isValid = checkNonEmpty( data, "BirthDate", errorContainer ) && isValid;
		isValid = checkNonEmpty( data, "Email", errorContainer ) && isValid;
		if ( isValid && !StringUtil.isEmailAddress( data.Email! ) ) {
			isValid = false;
			errorContainer[ "Email" ] = Strings.invalid_email_address;
		}
		isValid = checkNonEmpty( data, "MobileNumber", errorContainer ) && isValid;
		if ( isValid ) {
			const mobile = StringUtil.normaliseMobileNumber( data.MobileNumber! );
			if ( mobile && mobile.length < 12 ) {
				isValid = false;
				errorContainer[ "MobileNumber" ] = Strings.invalid_mobile_number;
			}
		}
		isValid = checkNonEmpty( data, "Pobox", errorContainer ) && isValid;
		isValid = checkNonEmpty( data, "NationalityId", errorContainer ) && isValid;
		if ( !this.state.isGCC ) {
			isValid = checkNonEmpty( data, "PassportNumber", errorContainer ) && isValid;
			isValid = checkNonEmpty( data, "PassportIssuePlace", errorContainer ) && isValid;
			isValid = checkNonEmpty( data, "PassportIssueDate", errorContainer ) && isValid;
			isValid = checkNonEmpty( data, "PassportExpiryDate", errorContainer ) && isValid;
			isValid = checkNonEmpty( data, "VisaNumber", errorContainer ) && isValid;
			isValid = checkNonEmpty( data, "VisaIssueDate", errorContainer ) && isValid;
			isValid = checkNonEmpty( data, "VisaExpiryDate", errorContainer ) && isValid;
		}
		isValid = checkNonEmpty( data, "EmiratesId", errorContainer ) && isValid;
		if ( isValid ) {
			if ( data.EmiratesId!.length !== 15 ) {
				isValid = false;
				errorContainer[ "EmiratesId" ] = Strings.invalid_emirateid;
			}
		}
		isValid = checkNonEmpty( data, "UIDNumber", errorContainer ) && isValid;
		this.setState( {
			errorContainer: errorContainer,
		} );
		return isValid;
	}

	private onSubmit = () => {
		if ( this.isValid() ) {
			showLoading();
			const data = this.getUpdatedData();
			const request = normalizeTenantRegistration( data );
			request.MobileNumber = request.MobileNumber!.replace( /^971/, "0" );
			request.ActiveIntegrationUserTypeId = 2;
			request.ActiveUserID = 0;
			trackEventWithData( "TenantContract", "TenantRegisterTry", { EmiratesId: request.UIDNumber } );
			EjariApi.registerTenant( request ).then( ( resp: TenantUserProfile ) => {
				hideLoading();
				const tenantId = resp.ParticipantId;
				if ( tenantId && tenantId !== 0 ) {
					dispatch( Actions.UPDATE_TENANT_ID, tenantId );
					trackEventWithData( "TenantContract", "TenantRegisterSuccess",
						{ EmiratesId: request.UIDNumber, TenantId: tenantId } );
					this.setState( {
						showConfirmation: true,
					} );
				} else if ( resp.ValidationError && resp.ValidationError.length > 0 ) {
					trackEventWithData( "TenantContract", "TenantRegisterFailed",
						{ EmiratesId: request.UIDNumber, errors: resp.ValidationError.join( "," ) } );
					showErrorMessage( Strings.an_error_occurred );
				}
			} ).catch( e => {
				trackEventWithData( "TenantContract", "TenantRegisterFailed", { EmiratesId: request.UIDNumber } );
				hideLoading();
				showErrorMessage( e.toString() );
			} );
		}
	};

	private onHide = () => {
		this.setState( {
			showConfirmation: false,
		}, () => this.onBack );
	};

	private onBack = () => {
		this.props.history.goBack();
	};

	private startTenantContact = () => {
		const { property } = this.context;
		const data = { property: property, action: TenancyContactAction.NEW_CONTRACT };
		resetTenantContractContext();
		updateTenantContractContext( data );
		replaceNavigate( "/mydld/tenant/contract" );
	};
}

export default TenantRegistration;
